import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setLocalStorage } from 'utils';
import { setUserLogged, setUserSacInfo, setLogout } from './operators';
import {
  postAuthUser,
  getSms,
  tokenPatient,
  getAlarmsQueue,
  postLogoutUser,
  postAuthUserIdp,
  postAuthUserIdpSelect,
  postAuthUserForgotPassword,
  putPushToken,
  IdpProvider,
} from "services/api";
import {
  ERROR_IDP_USER_NOT_MATCH,
  ERROR_USER_ACCESS_SUSPENDED,
  ERROR_WRONG_USER_OR_PASSWORD,
} from 'services/api/errorCodes';
import { getLocalStorage } from 'utils';

export type { IdpProvider };

export interface IdpCandidate {
  id: string;
  username: string;
  firstName: string;
  middleName: string;
  lastName: string;
  access: string;
  groupId: string;
  groupName: string;
}

export interface CommonState {
  user: {
    userLogged: any;
    sacInfo: any;
    status: 'idle' | 'loading' | 'failed';
  };
  idpProvider: null | IdpProvider;
  idpBearer: null | string;
  idpCandidates: null | IdpCandidate[];
  errorCode: number;
  session: null | 'success' | 'expired' | 'closed' | 'failed';
  sms: {
    data: null | any;
    total: number;
    status: null | 'idle' | 'loading' | 'failed';
  };
  tokenPatient: null | any;
  relationshipSelected: null | any;
  openRelationshipSelected: boolean;
  firebaseToken: null | any;
  openCommonModal: {
    data: null | any;
    openModal: boolean;
  };
  openChangePasswordModal: boolean;
  openEmailVerificationModal: boolean;
}

const initialState: CommonState = {
  user: {
    userLogged: getLocalStorage('userLogged'),
    sacInfo: getLocalStorage('sacInfo'),
    status: 'idle',
  },
  idpProvider: null,
  idpBearer: null,
  idpCandidates: null,
  errorCode: 0,
  session: null,
  sms: {
    data: null,
    total: 0,
    status: null,
  },
  tokenPatient: null,
  relationshipSelected: null,
  openRelationshipSelected: false,
  firebaseToken: null,
  openCommonModal: {
    data: null,
    openModal: false,
  },
  openChangePasswordModal: false,
  openEmailVerificationModal: false,
};

export const postAuthUserAsync = createAsyncThunk(
  'common/postAuthUser',
  async (data: any, { getState }) => {
  const state: any = getState();
  const firebaseToken = state.common.firebaseToken;
  const response = await postAuthUser({ ...data, pushRegistrationToken: firebaseToken });
  return response?.data;
});

export const postAuthUserForgotPasswordAsync = createAsyncThunk(
  'common/postAuthUserForgotPassword',
  async (data: any) => {
    const response = await postAuthUserForgotPassword({ username: data, });
    return response;
  });

export const postAuthUserIdpAsync = createAsyncThunk(
  'common/postAuthUserIdp',
  async (data: { provider: IdpProvider; bearer: string }) => {
    const response = await postAuthUserIdp(data.provider, { bearer: data.bearer });
    return response?.data;
  },
);

export const postAuthUserIdpSelectAsync = createAsyncThunk(
  'common/postAuthUserIdpSelect',
  async (data: { provider: IdpProvider; bearer: string; userId: string }) => {
    const response = await postAuthUserIdpSelect(data.provider, {
      bearer: data.bearer,
      userId: data.userId,
    });
    return response?.data;
  },
);

export const postLogoutUserAsync = createAsyncThunk('common/postLogoutUser', async () => {
  const response = await postLogoutUser();
  return response;
});

export const putPushTokenAsync = createAsyncThunk(
  'common/putPushToken',
  async (token: string | null) => {
    const response = await putPushToken({ token });
    return response?.data;
  },
);

export const getSmsAsync: any | null = createAsyncThunk('common/getSms', async (data: any) => {
  const params = {
    init: data.init,
    size: data.size,
  };
  const response = await getSms(data.id, params);
  return response;
});

export const tokenPatientAsync: any | null = createAsyncThunk(
  'common/tokenPatient',
  async (data: any) => {
    const response = await tokenPatient(data);
    return response;
  },
);

export const getSacInfoAsync: any | null = createAsyncThunk('common/getSacInfo', async (data: any) => {
  // SAC credentials inyectadas por el interceptor desde state.common.user.sacInfo.
  const response = await getAlarmsQueue(data.operatorId);
  return response;
});

export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    logoutAction: (state) => {
      state.user.userLogged = setLogout();
      state.session = 'closed';
      state.idpProvider = null;
      state.idpBearer = null;
      state.idpCandidates = null;
    },
    switchIdpAccount: (state) => {
      // Limpia la sesión TMC pero mantiene idpProvider/idpBearer/idpCandidates
      // para volver al selector sin re-hacer el flujo OAuth.
      // Importante: NO tocamos sessionStorage (cache de MSAL/Google vive ahí).
      setLocalStorage('userLogged', null);
      setLocalStorage('sacInfo', null);
      state.user.userLogged = null;
      state.user.sacInfo = null;
      state.session = null;
      state.errorCode = 0;
    },
    clearUserLogged: (state) => {
      state.user.userLogged = setLogout();
      state.session = 'expired';
    },
    setSessionExpired: (state, action: PayloadAction<number>) => {
      state.session = 'expired';
      state.errorCode = action.payload;
    },
    toggleChangePassword: (state)=> {
      state.openChangePasswordModal = !state.openChangePasswordModal;
    },
    toggleEmailVerification: (state)=> {
      state.openEmailVerificationModal = !state.openEmailVerificationModal;
    },
    setRelationshipsSelected: (state, action: PayloadAction<any>) => {
      state.relationshipSelected = action.payload;
    },
    setOpenRelationshipsSelected: (state, action: PayloadAction<boolean>) => {
      state.openRelationshipSelected = action.payload;
    },
    setFirebaseToken: (state, action: PayloadAction<any>) => {
      state.firebaseToken = action.payload;
    },
    setOpenCommonModal: (state, action: PayloadAction<any>) => {
      state.openCommonModal.data = action.payload.data;
      state.openCommonModal.openModal = action.payload.openModal;
    },
    setIdpBearer: (
      state,
      action: PayloadAction<{ provider: IdpProvider; bearer: string } | null>,
    ) => {
      if (action.payload === null) {
        state.idpProvider = null;
        state.idpBearer = null;
      } else {
        state.idpProvider = action.payload.provider;
        state.idpBearer = action.payload.bearer;
      }
    },
    clearIdpCandidates: (state) => {
      state.idpCandidates = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postAuthUserAsync.pending, (state) => {
        state.user.status = 'loading';
      })
      .addCase(postAuthUserAsync.fulfilled, (state, action) => {
        state.user.status = 'idle';
        state.user.userLogged = setUserLogged(action.payload);
        state.user.sacInfo = setUserSacInfo(action.payload);
        state.errorCode = action.payload?.errorCode;
        // 1009 = acceso bloqueado por un administrador. Sin contemplarlo aquí la sesión se daba por buena
        // y el usuario acababa en /home sin userLogged, con todas las peticiones fallando.
        state.session =
          action.payload?.errorCode === ERROR_WRONG_USER_OR_PASSWORD ||
          action.payload?.errorCode === ERROR_USER_ACCESS_SUSPENDED
            ? 'failed'
            : 'success';
      })
      .addCase(postAuthUserAsync.rejected, (state) => {
        state.user.status = 'failed';
      })
      .addCase(postAuthUserIdpAsync.pending, (state) => {
        state.user.status = 'loading';
        state.idpCandidates = null;
      })
      .addCase(postAuthUserIdpAsync.fulfilled, (state, action) => {
        state.user.status = 'idle';
        state.errorCode = action.payload?.errorCode;
        if (
          action.payload?.errorCode === ERROR_IDP_USER_NOT_MATCH ||
          action.payload?.errorCode === ERROR_USER_ACCESS_SUSPENDED
        ) {
          state.session = 'failed';
          return;
        }
        const item = action.payload?.item;
        if (item?.candidates?.length) {
          state.idpCandidates = item.candidates;
        } else if (item?.loginDto) {
          const wrapped = { item: item.loginDto };
          state.user.userLogged = setUserLogged(wrapped);
          state.user.sacInfo = setUserSacInfo(wrapped);
          state.session = 'success';
        }
      })
      .addCase(postAuthUserIdpAsync.rejected, (state) => {
        state.user.status = 'failed';
      })
      .addCase(postAuthUserIdpSelectAsync.pending, (state) => {
        state.user.status = 'loading';
      })
      .addCase(postAuthUserIdpSelectAsync.fulfilled, (state, action) => {
        state.user.status = 'idle';
        state.errorCode = action.payload?.errorCode;
        if (
          action.payload?.errorCode === ERROR_IDP_USER_NOT_MATCH ||
          action.payload?.errorCode === ERROR_USER_ACCESS_SUSPENDED
        ) {
          state.session = 'failed';
          return;
        }
        state.user.userLogged = setUserLogged(action.payload);
        state.user.sacInfo = setUserSacInfo(action.payload);
        // Mantenemos idpCandidates en memoria para permitir cambiar de cuenta
        // sin re-hacer el flujo OAuth.
        state.session = 'success';
      })
      .addCase(postAuthUserIdpSelectAsync.rejected, (state) => {
        state.user.status = 'failed';
      })
      .addCase(getSmsAsync.pending, (state) => {
        state.user.status = 'loading';
        state.sms.status = 'loading';
      })
      .addCase(getSmsAsync.fulfilled, (state, action) => {
        state.sms.status = 'idle';
        state.user.status = 'idle';
        state.sms.data = action.payload.data?.item?.items;
        state.sms.total = action.payload.data?.item?.total;
      })
      .addCase(getSmsAsync.rejected, (state) => {
        state.user.status = 'failed';
        state.sms.status = 'failed';
      })
      .addCase(tokenPatientAsync.pending, (state) => {
        state.user.status = 'loading';
      })
      .addCase(tokenPatientAsync.fulfilled, (state, action) => {
        state.user.status = 'idle';
        state.tokenPatient = action.payload?.data?.item;
      })
      .addCase(tokenPatientAsync.rejected, (state) => {
        state.user.status = 'failed';
      });
  },
});

export const {
  logoutAction,
  clearUserLogged,
  setOpenCommonModal,
  setSessionExpired,
  setRelationshipsSelected,
  setOpenRelationshipsSelected,
  setFirebaseToken,
  setIdpBearer,
  clearIdpCandidates,
  switchIdpAccount,
  toggleChangePassword,
  toggleEmailVerification
} = commonSlice.actions;

export default commonSlice.reducer;
